import type { CatalogImage } from '@/features/catalog-image/types';
import type { ProductVariant, CreateVariantInput } from '@/features/product-variant/types';
import type { ProductApprovalFields, VariantPricingDto } from '../commerce/types';
import type { PetSpecies } from '@/features/pet/types';

export type PharmacyProductCategory =
  | 'MEDICINE'
  | 'SUPPLEMENT'
  | 'PRESCRIPTION_DIET'
  | 'GROOMING'
  | 'WELLNESS'
  | 'OTHER';

export const PHARMACY_CATEGORY_OPTIONS: { value: PharmacyProductCategory; label: string }[] = [
  { value: 'MEDICINE', label: 'Medicine' },
  { value: 'SUPPLEMENT', label: 'Supplement' },
  { value: 'PRESCRIPTION_DIET', label: 'Prescription diet' },
  { value: 'GROOMING', label: 'Grooming' },
  { value: 'WELLNESS', label: 'Wellness' },
  { value: 'OTHER', label: 'Other' },
];

export type PharmacyProduct = ProductApprovalFields & {
  id: string;
  name: string;
  description: string | null;
  brand: string | null;
  pack_size: string | null;
  price_inr: string | null; // legacy base price — variants are authoritative
  category: PharmacyProductCategory;
  is_prescription_required: boolean;
  is_available: boolean;
  display_order: number;
  /** Which pets it is for; empty = all pets (client change, 9 Oct 2026). */
  species?: PetSpecies[];
  variants: ProductVariant[];
  images: CatalogImage[];
  created_at: string;
  updated_at: string;
  /** Per variant: what the vendor gets, the fee, and what the customer pays. */
  pricing?: VariantPricingDto[];
  /** Set when this listing came from the curated catalogue rather than typed. */
  catalog_product_id: string | null;
  is_from_catalog: boolean;
  catalog_image_path: string | null;
  /** Every curated photo, in order — what shoppers see before the vendor's own. */
  catalog_images?: { id: string; path: string }[];
};

export type CreateProductInput = {
  name: string;
  description?: string;
  brand?: string;
  pack_size?: string;
  category: PharmacyProductCategory;
  is_prescription_required?: boolean;
  is_available?: boolean;
  display_order?: number;
  /** Empty = all pets. Not reviewed: applies at once even on an approved product. */
  species?: PetSpecies[];
  variants: CreateVariantInput[]; // ≥1 required — price lives on variants
};

/**
 * Listing a curated master instead of typing a product out. Mutually exclusive with
 * CreateProductInput: the master owns the identity, so a name alongside a link is
 * refused by the API.
 */
export type LinkCatalogProductInput = {
  catalog_product_id: string;
  variants: CreateVariantInput[];
  is_available?: boolean;
};

/** Product-field updates only; variants are managed through the variant endpoints. */
export type UpdateProductInput = Partial<Omit<CreateProductInput, 'variants'>>;
